import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined"
import { Box, Grid, Link, Typography } from "@mui/material"
import React, { useContext, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { customPalette } from "../../../theme"
import { ProjectCardStatus } from "../../components/card/beem-master/project-status-card"
import { IconHome } from "../../components/custom-icons/icon-home"
import { IconInbox } from "../../components/custom-icons/icon-inbox"
import { theme } from "../../components/layout/BSLayout"
import { DefaultTypography } from "../../components/typography/default-typography"
import { pagesUrl } from "../../core/appConstants"
import { AppSideBarContext } from "../../core/context/AppSideBarContext/AppSideBarContext"
import { AppNavBarContext } from "../../core/context/nav-bar/AppNavBarContext"
import { UserContext } from "../../core/context/user/user-context"
import { SimpleInformationsProject } from "../../core/dto/simple-project-information/SimpleInformationsProject"
import { useHomePage } from "../../core/hooks/use-home-page"
import { formatToFrench } from "../../core/services/date-service"
import { displayIntegerNumber } from "../../core/services/helper-service"
import { resolveUrl } from "../../core/services/http-service"
import { ReferralCard } from "./components/ReferralCard"
import { PatchNotesList } from "./components/PatchNotesList/PatchNotesList"
import { NewProjectCard } from "./components/NewProjectCard"

export function HomePage(): React.JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()

  const { getHomePageIndicator, getLastProject } = useHomePage()

  const { setShowAppSide, setFullScreen } = useContext(AppSideBarContext)
  const { setTitle, setIcon } = useContext(AppNavBarContext)

  const { setShowNewFeatures } = useContext(UserContext)

  const [simpleProjectList, setSimpleProjectList] = useState<SimpleInformationsProject[]>()

  useEffect(() => {
    setShowAppSide(true)
    setTitle("Beem Shot")
    setIcon(<IconHome sx={{ color: theme.palette.primary.main, fontWeight: "2px" }} />)
    setFullScreen(true)
    return () => {
      setFullScreen(false)
    }
  }, [setShowAppSide, location, setTitle, setIcon, setFullScreen])

  useEffect(() => {
    getLastProject(3).then((projectList) => setSimpleProjectList(projectList))
  }, [getHomePageIndicator, getLastProject])

  function goToProject(project: SimpleInformationsProject): void {
    return navigate(resolveUrl(pagesUrl.PROJECT_PAGE_DETAIL, [project.projectId]))
  }

  return (
    <Grid container sx={{ pt: 1, pb: "3" }} columnSpacing={2} rowGap={1}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontSize="24px">
          Bienvenue 👋
        </Typography>
      </Grid>

      <Grid
        container
        item
        xs={12}
      >
        <Box sx={{ borderRadius: 3, backgroundColor: customPalette.white, boxShadow: 1, p: 1, width: "100%" }}>
          <Box display="flex" alignItems="center" gap={1} py={2}>
            <Typography color="text">Nouveauté 👉</Typography>
            <Link
              underline="hover"
              onClick={(e) => {
                e.preventDefault()
                setShowNewFeatures(true)
              }}
              sx={{
                cursor: "pointer",
              }}>
              Découvrez Time To Beem 3.0
            </Link>
          </Box>
          <PatchNotesList />
        </Box>
      </Grid>

      <Grid container item xs={12} rowGap={1}>
        <Grid item xs={12}>
          <Typography variant="h6">Derniers projets créés</Typography>
        </Grid>
        {simpleProjectList?.map((simpleProject) => (
          <Grid
            key={simpleProject.projectId}
            container
            rowGap={1}
            alignItems="center"
            borderRadius={3}
            p={1}
            onClick={() => goToProject(simpleProject)}
            justifyContent="space-between"
            sx={{
              backgroundColor: "white",
              boxShadow: "2px 4px 20px 0px rgba(0, 0, 0, 0.05)",
              transition: "all 0.2s ease-in-out",
              ":hover": {
                cursor: "pointer",
                boxShadow: 10,
                transform: "translateY(-2px)",
              },
            }}>
            <Grid item display="flex" xs={4} columnGap={3} rowGap={2}>
              <Box
                display="flex"
                alignItems="center"
                sx={{ borderRadius: 2, background: "#b7ffe3", p: 1, borderColor: "white" }}>
                <IconInbox sx={{ color: theme.palette.primary.main, fontWeight: "2px" }} />
              </Box>

              <Box>
                <Typography variant="subtitle1">{simpleProject.projectName}</Typography>

                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  alignContent="center"
                  columnGap={0.5}
                  justifyItems="center">
                  <DefaultTypography label="Crée par " color="#070F27" fontWeight={400} fontSize="11px" />
                  <DefaultTypography
                    label={simpleProject?.createdByUser ?? ""}
                    color="#070F27"
                    fontWeight={500}
                    fontSize="11px"
                  />
                  <Typography variant="subtitle1" fontSize={11}>
                    {`· ${simpleProject?.organizationName} · `}
                  </Typography>

                  <Typography variant="subtitle1" fontSize={11} fontWeight={400} sx={{ color: "#8F8F8F" }}>
                    {`Le ${formatToFrench(simpleProject.createdDate)} `}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid container item xs={2} display="flex" justifyContent="flex-end" alignItems="center" columnGap={3}>
              <Box display="flex" alignItems="center" sx={{ background: "#000", px: 1, borderRadius: 2 }}>
                <PersonOutlinedIcon fontSize="small" sx={{ color: "#fff" }} />
                <DefaultTypography
                  label={displayIntegerNumber(simpleProject.teamNumber)}
                  fontSize="14px"
                  lineHeight="20.02px"
                  fontWeight={400}
                  color="#ffffff"
                />
              </Box>

              <ProjectCardStatus status={simpleProject.status} />
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Grid item xs={8}>
        <NewProjectCard />
      </Grid>

      <Grid item xs={4}>
        <ReferralCard />
      </Grid>
    </Grid>
  )
}
